import React, { FC, RefObject, useEffect, useMemo, useState } from 'react';
import 'devextreme/data/odata/store';
import './PredefinedFilters.scss';
import Button from '@atoms/Button';
import { IListElement } from '@/types';
import Menu from '@atoms/Menu';
import { MdOutlineExpandMore, MdStar, MdStarOutline } from 'react-icons/md';
import { IPredefinedFilter } from '@models/response/IPredefinedFiltersItems';
import { DataGrid } from 'devextreme-react/data-grid';
import Tooltip from '@atoms/Tooltip';
import clsx from 'clsx';

export interface IPredefinedFiltersProps {
    /**
     * Функция получения списка фильтров
     * */
    loadFilters?: () => IPredefinedFilter[] | undefined;

    /**
     * Выбранный фильтр
     * */
    selectedFilter?: IPredefinedFilter;

    /**
     * Фильтр выбранный по умолчанию
     * */
    defaultFilterKey?: string;

    /**
     * Событие изменения выбранного фильтра
     * */
    onChangeSelected?: (filter: IPredefinedFilter | undefined) => void;

    /**
     * Событие изменения фильтра по умолчанию
     * */
    onChangeDefault?: (filter: IPredefinedFilter | undefined) => void;

    /**
     * Ссылка на компонент родительского грида
     */
    gridRef?: RefObject<DataGrid | null>;
}

const PredefinedFilters: FC<IPredefinedFiltersProps> = ({
    loadFilters,
    selectedFilter,
    defaultFilterKey,
    onChangeSelected,
    onChangeDefault,
    gridRef,
}: IPredefinedFiltersProps) => {
    const [filters, setFilters] = useState<IPredefinedFilter[] | undefined>();
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

    useEffect(() => {
        const _filters = loadFilters?.();
        setFilters(_filters);
    }, [loadFilters]);

    const filtersList = useMemo(() => {
        return (
            filters?.map((filter, index) => {
                const isDefaultFilter = filter.key === defaultFilterKey;
                return {
                    label: filter.name,
                    value: filter.key,
                    handler: () => {
                        onChangeSelected?.(filter);
                    },
                    endAdornment: () => (
                        <div className="predefined-filters-element__default-button-wrapper">
                            <Tooltip background="black" position="bottom" openDelay={500}>
                                <Button
                                    buttonType="icon-round"
                                    size="xxs"
                                    aria-label={
                                        isDefaultFilter ? 'Отменить выбор по умолчанию' : 'Выбрать по умолчанию'
                                    }
                                    onClick={() => {
                                        onChangeDefault && onChangeDefault(isDefaultFilter ? undefined : filter);
                                    }}
                                    className={clsx(
                                        'predefined-filters-element__default-button',
                                        isDefaultFilter && 'predefined-filters-element__default-button--active',
                                    )}
                                    startAdornment={<MdStar size="20" />}
                                />
                                {isDefaultFilter ? 'Отменить выбор по умолчанию' : 'Выбрать по умолчанию'}
                            </Tooltip>
                        </div>
                    ),
                } as IListElement;
            }) ?? []
        );
    }, [defaultFilterKey, filters, onChangeDefault, onChangeSelected]);

    useEffect(() => {
        // записываем ключ фильтра в атрибут data-filter родительского контейнера (для кастомизации грида стилями)
        const gridWrapper = gridRef?.current?.instance.element().firstElementChild;
        if (selectedFilter) {
            gridWrapper?.setAttribute('data-filter', selectedFilter.key);
        } else {
            gridWrapper?.removeAttribute('data-filter');
        }
    }, [selectedFilter, gridRef]);

    return filtersList.length > 0 ? (
        <Menu
            key="predefinedFiltersMenu"
            className="predefined-filters-menu__content"
            list={filtersList}
            position="bottom-end"
            onToggleMenu={(show) => {
                setIsMenuOpen(show);
            }}
        >
            <div className="predefined-filters-menu">
                <Button
                    buttonType="round"
                    textColor="neutral"
                    size="s"
                    startAdornment={<MdOutlineExpandMore size="20" />}
                    endAdornment={
                        selectedFilter?.key === defaultFilterKey ? (
                            isMenuOpen ? (
                                <MdStarOutline size="20" />
                            ) : (
                                <Tooltip background="black" position="bottom" openDelay={500}>
                                    <MdStarOutline size="20" />
                                    Этот фильтр выбран фильтром по умолчанию
                                </Tooltip>
                            )
                        ) : undefined
                    }
                >
                    {selectedFilter?.name}
                </Button>
            </div>
        </Menu>
    ) : (
        <></>
    );
};

export default PredefinedFilters;
